/**
 * The router's root error boundary. Catches a throw that escaped everything else, including one
 * from the shell itself.
 *
 * It offers a reload rather than a retry: at this level the app's state is not trustworthy, and
 * the cart is the only thing worth mourning — which is why it says so.
 */

import { isRouteErrorResponse, useRouteError } from 'react-router';

import { Button } from '@/shared/ui';

import { NotFoundPage } from './NotFoundPage.js';

export function RootErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;

  const detail =
    error instanceof Error
      ? error.message
      : isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}`
        : 'No further detail was reported.';

  return (
    <main className="grid min-h-dvh place-items-center bg-surface-backdrop px-6">
      <div className="w-full max-w-column rounded-sheet bg-surface-base p-[1.75rem] text-center shadow-column">
        <h1 className="text-xl font-bold text-content-primary">The app stopped unexpectedly</h1>

        <p className="mt-3 text-sm leading-relaxed text-content-secondary">
          No sale was charged. Reloading gets you back to the till — anything that was in the cart
          will need ringing up again.
        </p>

        <Button
          size="lg"
          fullWidth
          className="mt-6"
          onClick={() => {
            window.location.assign('/sell');
          }}
        >
          Reload the till
        </Button>

        {/* Shown, not hidden: the owner is the developer's only channel, and "it just broke" is
            not a bug report. */}
        <details className="mt-5 text-left">
          <summary className="cursor-pointer text-xs font-semibold text-content-muted">
            Technical detail
          </summary>
          <pre className="mt-2 overflow-x-auto rounded-md bg-surface-sunken p-3 text-xs text-content-muted">
            {detail}
          </pre>
        </details>
      </div>
    </main>
  );
}
